import { usePermissionsStore } from "../../permissions/permissions_store";
import classnames from "classnames";
import moment from "moment";
import { Image } from "uikit-react";

import FeatherIcon from "../../common/FeatherIcon";
import { messageThreadRecipientType } from "../message-type-filters";

const getParticipantName = (participant) =>
  [participant?.first_name, participant?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  participant?.email ||
  "";

export const getTeamThreadTitle = (thread, personalInboxId) => {
  const label = String(thread?.label || "").trim();
  if (label) return label;

  const names = (thread?.participants || [])
    .filter(
      (participant) =>
        String(participant?.personal_inbox_id) !== String(personalInboxId),
    )
    .map(getParticipantName)
    .filter(Boolean);

  if (!names?.length) return "Private conversation";
  if ((names?.length || 0) <= 3) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${(names?.length || 0) - 2}`;
};

export const SMSThreadCard = ({
  thread,
  selected,
  handleSelect,
  togglePersonalMeta,
  setPersonalMeta,
  isMetaPending,
  personalInboxId,
}) => {
  const { canEdit } = usePermissionsStore();
  const canManage = canEdit("messaging.messages");
  var {
    contact_first_name,
    contact_last_name,
    guest_first_name,
    guest_last_name,
    formatted_contact_phone,
    formatted_guest_phone,
    last_msg,
  } = thread;

  const isTeamThread = thread.conversation_type === "team";
  const recipientType = messageThreadRecipientType(thread);
  const isGuestThread = recipientType === "guest";
  var firstName = isTeamThread
    ? getTeamThreadTitle(thread, personalInboxId)
    : isGuestThread
      ? guest_first_name ||
        contact_first_name ||
        formatted_guest_phone ||
        formatted_contact_phone ||
        "[Unknown]"
      : contact_first_name ||
        guest_first_name ||
        formatted_contact_phone ||
        formatted_guest_phone ||
        "[Unknown]";

  var lastName = isTeamThread
    ? ""
    : isGuestThread
      ? guest_last_name || contact_last_name || ""
      : contact_last_name || guest_last_name || "";
  const isUnread = thread.meta_values?.indexOf("unread") >= 0;
  const readPending = isMetaPending(thread.id, "unread");
  const priorityPending = isMetaPending(thread.id, "priority");
  const archivePending = isMetaPending(thread.id, "archived");

  return (
    <div
      className={classnames("chat-guest-contact-wrapper", {
        "active-chat-guest-contact": selected,
        "thread-unread": isUnread,
      })}
      key={thread.id}
    >
      <div
        className="message-text-group"
        onClick={() => {
          handleSelect(thread.id);
        }}
      >
        <span
          className="message-text-group-icon"
          uk-tooltip={`title: ${
            isTeamThread
              ? "Private Conversation"
              : recipientType === "lead"
                ? "Lead"
                : recipientType === "contact"
                  ? thread.contact_category
                      ?.toLowerCase()
                      .replace(/\b\w/g, (s) => s.toUpperCase()) || "Contact"
                  : "Guest"
          }; pos: bottom-left; cls: uk-active contact-tooltip`}
        >
          {isTeamThread ? (
            <FeatherIcon name="users" color="inherit" />
          ) : !isGuestThread && thread.contact_category !== null ? (
            <Image src={`/images/${thread.contact_category}-active.svg`} />
          ) : (
            <Image src="/images/guest-active.svg" />
          )}
        </span>
        <div>
          <div className="semi-bold-font">
            {[firstName, lastName].join(" ")}
          </div>
          {!isTeamThread &&
            isGuestThread &&
            thread.current_reservation_count > 0 &&
            (thread.current_reservation_count > 1 ? (
              <div className="chat-guest-contact-address uk-margin-tiny-top uk-margin-tiny-bottom">
                Multiple Current Bookings
                <img
                  class="info-icon"
                  src="/images/info-icon.svg"
                  alt="info"
                  uk-tooltip={`${thread.current_reservation_count} Current Bookings. See the right side for Property IDs.`}
                  title=""
                  aria-describedby="uk-tooltip-134"
                  tabindex="0"
                />
              </div>
            ) : (
              <div
                className="chat-guest-contact-address uk-margin-tiny-top uk-margin-tiny-bottom"
                uk-tooltip={thread?.unit_code}
              >
                {(thread?.unit_code || "").length > 30
                  ? `${thread.unit_code.slice(0, 30)}`
                  : thread?.unit_code}
              </div>
            ))}
          <div
            className={
              isUnread ? "chat-guest-unread-message" : "chat-guest-message"
            }
          >
            {(last_msg || "").length > 60
              ? `${last_msg.slice(0, 60)}...`
              : last_msg}
          </div>
          <div className="chat-guest-handled">
            {thread.meta_values.indexOf("unhandled") === -1 && (
              <span aria-label="Handled" uk-tooltip="Handled">
                <FeatherIcon
                  name="check"
                  className="handled-icon"
                  color="#28a745"
                />
              </span>
            )}
          </div>
          <div className="chat-guest-contact-date">
            {moment
              .utc(thread.last_message_on)
              .local()
              .format("MMM DD, YYYY hh:mm a")}
          </div>
        </div>
      </div>
      <div className="messages-btn-group">
        <button
          type="button"
          className="uk-button hms-simple-icon-btn inbox-read-state-indicator"
          onClick={() => setPersonalMeta(thread.id, "unread", !isUnread)}
          disabled={!canManage || readPending}
          aria-busy={readPending}
          uk-tooltip={isUnread ? "Mark as Read" : "Mark as Unread"}
        >
          {readPending ? (
            <span uk-spinner="ratio: 0.6" aria-label="Updating read status" />
          ) : isUnread ? (
            <FeatherIcon
              name="eye-off"
              className="inbox-action-icon inbox-read-state-icon"
              color="inherit"
            />
          ) : (
            <FeatherIcon
              name="eye"
              className="inbox-action-icon inbox-read-state-icon"
              color="inherit"
            />
          )}
        </button>
        <button
          className={classnames("uk-button hms-simple-icon-btn", {
            "icon-active icon-priority":
              thread.meta_values.indexOf("priority") >= 0,
          })}
          onClick={() => togglePersonalMeta(thread.id, "priority", true)}
          disabled={!canManage || priorityPending}
          aria-busy={priorityPending}
          uk-tooltip={
            thread.meta_values.indexOf("priority") >= 0
              ? "Unprioritize"
              : "Prioritize"
          }
        >
          {priorityPending ? (
            <span uk-spinner="ratio: 0.6" aria-label="Updating priority" />
          ) : (
            <FeatherIcon
              name="flag"
              className="inbox-action-icon"
              color="inherit"
            />
          )}
        </button>
        <button
          className={classnames("uk-button hms-simple-icon-btn", {
            "icon-active icon-archived":
              thread.meta_values.indexOf("archived") >= 0,
          })}
          onClick={() => togglePersonalMeta(thread.id, "archived")}
          disabled={!canManage || archivePending}
          aria-busy={archivePending}
          uk-tooltip={
            thread.meta_values.indexOf("archived") >= 0
              ? "Unarchive"
              : "Archive"
          }
        >
          {archivePending ? (
            <span
              uk-spinner="ratio: 0.6"
              aria-label="Updating archive status"
            />
          ) : (
            <FeatherIcon
              name="archive"
              className="inbox-action-icon"
              color="inherit"
            />
          )}
        </button>
      </div>
    </div>
  );
};
